import { Component, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { HttpService } from "src/app/services/http-service";

export interface ReaderSettingsModalData {
  languageCode: string;
  waitPerBeadMs: number;
  languageOptions: Array<{ value: string; label: string; flag: string }>;
  timeOptions: number[];
}

export interface ReaderSettingsModalResult {
  languageCode: string;
  waitPerBeadMs: number;
}

@Component({
  selector: "reader-settings-modal",
  templateUrl: "./reader-settings-modal.html",
  styleUrls: ["./reader-settings-modal.css"],
})
export class ReaderSettingsModal implements OnInit {
  selectedLanguageCode: string;
  selectedWaitPerBeadMs: number;
  waitPerBeadStepMs = 500;
  timeOptionLabels: Map<number, string> = new Map();

  constructor(
    public httpService: HttpService,
    public dialogRef: MatDialogRef<ReaderSettingsModal>,
    @Inject(MAT_DIALOG_DATA) public data: ReaderSettingsModalData,
  ) {
    this.selectedLanguageCode = data.languageCode || "en-US";
    this.selectedWaitPerBeadMs = data.waitPerBeadMs;
  }

  ngOnInit(): void {
    if (this.data.timeOptions.length > 1) {
      this.waitPerBeadStepMs = this.data.timeOptions[1] - this.data.timeOptions[0];
    }
    this.selectedWaitPerBeadMs = this.normalizeWaitPerBeadMs(this.selectedWaitPerBeadMs);

    this.data.timeOptions.forEach(option => {
      const seconds = option / 1000;
      this.timeOptionLabels.set(option, `${seconds}s`);
    });
  }

  onWaitPerBeadChange(value: number | null): void {
    if (value == null) return;
    this.selectedWaitPerBeadMs = this.normalizeWaitPerBeadMs(value);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.dialogRef.close({
      languageCode: this.selectedLanguageCode,
      waitPerBeadMs: this.selectedWaitPerBeadMs,
    } as ReaderSettingsModalResult);
  }

  getTimeOptionLabel(value: number): string {
    return this.timeOptionLabels.get(value) || `${value / 1000}s`;
  }

  getSelectedLanguageOption(): { value: string; label: string; flag: string } | undefined {
    return this.data.languageOptions.find(option => option.value === this.selectedLanguageCode);
  }

  private normalizeWaitPerBeadMs(value: number): number {
    if (!this.data.timeOptions.length) return value;
    const min = this.data.timeOptions[0];
    const max = this.data.timeOptions[this.data.timeOptions.length - 1];
    const clamped = Math.min(Math.max(Number(value) || min, min), max);
    const snapped = Math.round((clamped - min) / this.waitPerBeadStepMs) * this.waitPerBeadStepMs + min;
    return Math.min(Math.max(snapped, min), max);
  }
}
